import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { Alert, Button, Select, Tooltip, Typography } from 'antd';
import styled from '@emotion/styled';
import { useMutation } from '@apollo/client/react';

import BankAccountNumberDisplay from './BankAccountNumberDisplay';
import EditFundingAccountsConfirmationModal, {
  FUNDING_ACCOUNT_STATUS_OPTIONS,
} from './EditFundingAccountsConfirmationModal';

import AddFundingAccountModal from 'components/AddFundingAccountModal/AddFundingAccountModal';
import CondensedTable from 'components/CondensedTable/CondensedTable';
import Pill from 'components/Pill/Pill';
import useModalControls from 'hooks/useModalControls';
import {
  EDIT_FUNDING_ACCOUNT,
  EDIT_FUNDING_ACCOUNTS,
} from 'mutations/fundingAccounts';
import { ACCOUNT_STATUS, CHANNEL } from 'utilities/constants';
import { formatDateTime } from 'utilities/datesAndTimes';

const IdText = styled(Typography.Text)`
  display: inline-block;
  .ant-typography-copy {
    vertical-align: baseline;
  }
`;

const getTableColumns = (onSetDefault, setDefaultFundingAccountLoading) => {
  return [
    {
      title: 'ID',
      dataIndex: 'id',
      render: (id) => <IdText copyable={true}>{id}</IdText>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (text) => <StatusTag status={text} />,
    },
    {
      title: 'Bank',
      dataIndex: 'bankName',
    },
    {
      title: 'Routing Number',
      dataIndex: 'routingNumber',
    },
    {
      title: 'Account Last 4',
      dataIndex: 'numberLast4',
      render: (numberLast4, record) => (
        <BankAccountNumberDisplay
          fundingAccountId={String(record.id)}
          numberLast4={numberLast4}
        />
      ),
    },
    {
      title: 'Date Added',
      dataIndex: 'createdDate',
      render: formatDateTime,
    },
    {
      title: 'Actions',
      render: (account) => {
        if (account.status !== ACCOUNT_STATUS.ENABLED) {
          return null;
        }
        return (
          <Button
            data-testid={`set-default-button-${account.id}`}
            type="link"
            size="small"
            disabled={setDefaultFundingAccountLoading}
            onClick={() => onSetDefault(account)}
          >
            Set Default
          </Button>
        );
      },
    },
  ];
};

const StatusTag = ({ status }) => {
  switch (status) {
    case ACCOUNT_STATUS.DEFAULT:
      return <Pill color="green">Default</Pill>;
    case ACCOUNT_STATUS.DISABLED:
      return <Pill color="red">Disabled</Pill>;
    case ACCOUNT_STATUS.NON_OPERATIONAL:
      return <Pill color="yellow">Non-Operational</Pill>;
    case ACCOUNT_STATUS.ENABLED:
      return <Pill color="blue">Enabled</Pill>;
    default:
      return <Pill>{status}</Pill>;
  }
};

const FundingAccountTable = ({
  fundingAccounts = [],
  canAddFundingAccount = false,
  loading,
}) => {
  const { customerId } = useParams();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [editStatus, setEditStatus] = useState(undefined);
  const addFundingAccountModal = useModalControls();
  const editFundingAccountsModal = useModalControls();

  const [
    editFundingAccounts,
    {
      data: editData,
      loading: editLoading,
      error: editError,
      reset: resetEditError,
    },
  ] = useMutation(EDIT_FUNDING_ACCOUNTS, {
    refetchQueries: ['GetCustomerFundingAccounts', 'AccountHistory'],
  });

  const [
    setDefaultFundingAccount,
    {
      loading: setDefaultFundingAccountLoading,
      error: setDefaultFundingAccountError,
    },
  ] = useMutation(EDIT_FUNDING_ACCOUNT, {
    refetchQueries: ['GetCustomerFundingAccounts'],
  });

  const selectedAccounts = useMemo(
    () => fundingAccounts.filter((a) => selectedRowKeys.includes(a.id)),
    [fundingAccounts, selectedRowKeys],
  );

  const handleSetDefault = async (account) => {
    try {
      await setDefaultFundingAccount({
        variables: {
          customerId,
          fundingAccountId: String(account.id),
          status: ACCOUNT_STATUS.DEFAULT,
          channel: CHANNEL.AGENT,
        },
      });
    } catch {
      // error state handled by setDefaultFundingAccountError from useMutation
    }
  };

  const openEditConfirmation = (status) => {
    setEditStatus(status);
    editFundingAccountsModal.show();
  };

  const handleEditFundingAccountsConfirm = async () => {
    try {
      const { data } = await editFundingAccounts({
        variables: {
          customerId,
          fundingAccountIds: selectedRowKeys.map(String),
          fundingAccountStatus: editStatus,
        },
      });

      const { status } = data.editFundingAccounts;
      if (status === 'SUCCESS') {
        setSelectedRowKeys([]);
        setEditStatus(undefined);
        editFundingAccountsModal.hide();
      }
      // PARTIAL_SUCCESS / FAILURE — keep modal open so user sees the error alert
    } catch {
      // error state handled by editError from useMutation
    }
  };

  const columns = getTableColumns(
    handleSetDefault,
    setDefaultFundingAccountLoading,
  );

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  return (
    <>
      <AddFundingAccountModal
        {...addFundingAccountModal}
        refetchQueries={['GetCustomerFundingAccounts']}
      />
      <EditFundingAccountsConfirmationModal
        open={editFundingAccountsModal.open}
        accounts={selectedAccounts}
        status={editStatus}
        loading={editLoading}
        error={
          editError ||
          (editData && editData.editFundingAccounts?.status !== 'SUCCESS')
        }
        onConfirm={handleEditFundingAccountsConfirm}
        onCancel={() => {
          setEditStatus(undefined);
          resetEditError();
          editFundingAccountsModal.hide();
        }}
      />
      <CondensedTable
        loading={loading}
        columns={columns}
        rowKey={(record) => record.id}
        data={fundingAccounts}
        showSelectionActions={true}
        showCopyButton={false}
        selectionActions={[
          {
            render: ({ disabled }) => (
              <Tooltip
                title={
                  disabled ? 'Select one or more accounts to edit' : undefined
                }
              >
                <Select
                  data-testid="edit-funding-accounts-status-select"
                  placeholder={`Edit funding account${selectedRowKeys.length !== 1 ? 's' : ''}`}
                  options={FUNDING_ACCOUNT_STATUS_OPTIONS}
                  value={editStatus}
                  onChange={openEditConfirmation}
                  disabled={disabled}
                />
              </Tooltip>
            ),
          },
        ]}
        rowSelection={rowSelection}
      />
      {setDefaultFundingAccountError && (
        <Alert
          banner
          type="error"
          title="Failed to set default funding account. Please try again."
          style={{ marginTop: 'var(--spacing-sm)' }}
        />
      )}
      {canAddFundingAccount && (
        <Button
          type="primary"
          onClick={addFundingAccountModal.show}
          data-testid="add-funding-account-button"
          style={{ marginTop: 'var(--spacing)' }}
        >
          Add Funding Account
        </Button>
      )}
    </>
  );
};

export default FundingAccountTable;
